import type { z } from "zod";

import {
  canonicalJson,
  compareCodeUnits,
  sha256Hex,
  type JsonValue,
} from "./canonical.js";
import {
  CalcDecimal,
  decimalString,
  moneyString,
  percentString,
} from "./decimal.js";
import {
  resolvedCalculationInputSchema,
  type ResolvedCalculationInput,
  type ResolvedComponentInput,
} from "./schema.js";

export const ENGINE_VERSION = "0.1.0";
export const FORMULA_ID = "dm140-2012-cp-v1";

const P_BASE = new CalcDecimal("0.03");
const P_NUMERATOR = new CalcDecimal("10");
const P_EXPONENT = new CalcDecimal("0.4");

export interface NormalizedQItem {
  readonly code: string;
  readonly value: string;
}

export interface NormalizedComponentInput {
  readonly componentId: string;
  readonly label: string;
  readonly valueV: string;
  readonly complexityG: string;
  readonly qItems: readonly NormalizedQItem[];
}

export interface NormalizedCalculationInput {
  readonly rulesetVersion: string;
  readonly components: readonly NormalizedComponentInput[];
}

export interface ComponentResult extends NormalizedComponentInput {
  readonly sortOrder: number;
  readonly qTotal: string;
  readonly parameterP: string;
  readonly parameterPPercentDisplay: string;
  readonly compensationRaw: string;
  readonly compensationRounded: string;
}

export interface CalculationResult {
  readonly engineVersion: string;
  readonly formulaId: string;
  readonly rulesetVersion: string;
  readonly normalizedInput: NormalizedCalculationInput;
  readonly components: readonly ComponentResult[];
  readonly totals: {
    readonly valueV: string;
    readonly compensationRaw: string;
    readonly compensationRawRounded: string;
    readonly compensationRounded: string;
    readonly roundingDelta: string;
  };
}

export interface HashedCalculationResult extends CalculationResult {
  readonly inputSha256: string;
  readonly resultSha256: string;
}

export class CalculationInputError extends Error {
  readonly issues: z.core.$ZodIssue[];

  constructor(issues: z.core.$ZodIssue[]) {
    super("Calculation input is invalid");
    this.name = "CalculationInputError";
    this.issues = issues;
  }
}

function normalizeComponent(
  component: ResolvedComponentInput,
): NormalizedComponentInput {
  const qItems = component.qItems
    .map((item) => ({
      code: item.code,
      value: decimalString(new CalcDecimal(item.value)),
    }))
    .sort((left, right) => compareCodeUnits(left.code, right.code));

  return {
    componentId: component.componentId,
    label: component.label,
    valueV: decimalString(new CalcDecimal(component.valueV)),
    complexityG: decimalString(new CalcDecimal(component.complexityG)),
    qItems,
  };
}

function calculateComponent(
  component: NormalizedComponentInput,
  sortOrder: number,
): ComponentResult {
  const valueV = new CalcDecimal(component.valueV);
  const complexityG = new CalcDecimal(component.complexityG);
  const qTotal = component.qItems.reduce(
    (total, item) => total.plus(item.value),
    new CalcDecimal(0),
  );
  const parameterP = P_BASE.plus(P_NUMERATOR.div(valueV.pow(P_EXPONENT)));
  const compensationRaw = valueV
    .times(parameterP)
    .times(complexityG)
    .times(qTotal);

  return {
    ...component,
    sortOrder,
    qTotal: decimalString(qTotal),
    parameterP: decimalString(parameterP),
    parameterPPercentDisplay: percentString(parameterP),
    compensationRaw: decimalString(compensationRaw),
    compensationRounded: moneyString(compensationRaw),
  };
}

function deepFreeze<T>(value: T): Readonly<T> {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value).forEach((item) => deepFreeze(item));
  }
  return value;
}

export function calculateResolved(input: unknown): CalculationResult {
  const parsed = resolvedCalculationInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new CalculationInputError(parsed.error.issues);
  }

  const normalizedInput: NormalizedCalculationInput = {
    rulesetVersion: parsed.data.rulesetVersion,
    components: parsed.data.components.map(normalizeComponent),
  };
  const components = normalizedInput.components.map(calculateComponent);

  const valueTotal = components.reduce(
    (total, component) => total.plus(component.valueV),
    new CalcDecimal(0),
  );
  const rawTotal = components.reduce(
    (total, component) => total.plus(component.compensationRaw),
    new CalcDecimal(0),
  );
  const roundedTotal = components.reduce(
    (total, component) => total.plus(component.compensationRounded),
    new CalcDecimal(0),
  );
  const rawTotalRounded = new CalcDecimal(moneyString(rawTotal));

  return deepFreeze({
    engineVersion: ENGINE_VERSION,
    formulaId: FORMULA_ID,
    rulesetVersion: normalizedInput.rulesetVersion,
    normalizedInput,
    components,
    totals: {
      valueV: decimalString(valueTotal),
      compensationRaw: decimalString(rawTotal),
      compensationRawRounded: moneyString(rawTotal),
      compensationRounded: moneyString(roundedTotal),
      roundingDelta: moneyString(roundedTotal.minus(rawTotalRounded)),
    },
  });
}

export async function calculateResolvedWithHashes(
  input: unknown,
): Promise<HashedCalculationResult> {
  const result = calculateResolved(input);
  const inputSha256 = await sha256Hex(
    canonicalJson(result.normalizedInput as unknown as JsonValue),
  );
  const resultSha256 = await sha256Hex(
    canonicalJson(result as unknown as JsonValue),
  );

  return deepFreeze({
    ...result,
    inputSha256,
    resultSha256,
  });
}

export type { ResolvedCalculationInput };
